import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import yaml from 'js-yaml';
import { StructuredOutputTree } from './structuredOutputTree';
import { StructuredOutputForm } from './structuredOutputForm';
import { schemaToTreeItems, treeItemsToSchema } from './struturedOutputFactory';
import { fileToJsonSchema } from './utils';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useGenerateMutation } from 'services/assistantApi';
import { useNotification } from 'hook/useNotification';

/**
 * Main part for building structured output configuration
 */
export function StructuredOutputBuilder({ initialSchema = null, onChange = () => {} }) {
  const { formatMessage: msg } = useIntl();
  const actualInitialSchema = initialSchema;
  const { addNotification } = useNotification();

  const [treeItems, setTreeItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [schema, setSchema] = useState(actualInitialSchema);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      title: actualInitialSchema?.title || '',
      description: actualInitialSchema?.description || '',
      format: actualInitialSchema?.format || 'json'
    }
  });

  const title = watch('title');
  const description = watch('description');
  const format = watch('format');

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Fix infinite loop: watch individual fields instead of array
  useEffect(() => {
    setSchema((prev) => ({
      ...prev,
      title,
      description,
      format
    }));
  }, [title, description, format]);

  useEffect(() => {
    if (onChange) {
      onChange(schema);
    }
  }, [schema, onChange]);

  // Initialize tree items from a schema
  useEffect(() => {
    const items = schemaToTreeItems(actualInitialSchema);
    setTreeItems(items);
  }, [actualInitialSchema]);

  const findItemById = useCallback((items, id) => {
    if (!items || !id) return null;

    const directMatch = items.find((item) => item.id === id);
    if (directMatch) return directMatch;

    for (let i = 0; i < items.length; i += 1) {
      if (items[i].children && items[i].children.length) {
        const found = findItemById(items[i].children, id);
        if (found) return found;
      }
    }

    return null;
  }, []);

  const updateItemInTree = useCallback(
    (items, updatedItem) =>
      items.map((item) => {
        if (item.id === updatedItem.id) {
          return { ...updatedItem };
        }
        if (item.children && item.children.length) {
          return {
            ...item,
            children: updateItemInTree(item.children, updatedItem)
          };
        }
        return item;
      }),
    []
  );

  const handleSelectItem = useCallback((itemId) => {
    setSelectedItemId(itemId);
  }, []);

  const handleUpdateItem = useCallback(
    (updatedItem) => {
      const newTreeItems = updateItemInTree(treeItems, updatedItem);
      setTreeItems(newTreeItems);

      const newSchema = {
        ...schema,
        ...treeItemsToSchema(newTreeItems)
      };

      setSchema(newSchema);
      if (onChange) {
        onChange(newSchema);
      }
    },
    [onChange, schema, treeItems, updateItemInTree]
  );

  const onFormSubmit = useCallback(
    (data) => {
      const updatedSchema = {
        ...schema,
        title: data.title,
        description: data.description,
        format: data.format
      };

      setSchema(updatedSchema);
      if (onChange) {
        onChange(updatedSchema);
      }
    },
    [onChange, schema]
  );

  const addItemToTree = useCallback(
    (items, parentId, newItem) =>
      items.map((item) => {
        if (item.id === parentId) {
          return {
            ...item,
            children: [...(item.children || []), newItem]
          };
        }

        if (item.children && item.children.length) {
          return {
            ...item,
            children: addItemToTree(item.children, parentId, newItem)
          };
        }

        return item;
      }),
    []
  );

  const deleteItemFromTree = useCallback((items, itemId) => {
    const filteredItems = items.filter((item) => item.id !== itemId);

    if (filteredItems.length === items.length) {
      return items.map((item) => {
        if (item.children && item.children.length) {
          return {
            ...item,
            children: deleteItemFromTree(item.children, itemId)
          };
        }
        return item;
      });
    }

    return filteredItems;
  }, []);

  const handleAddRootItem = useCallback(() => {
    const newItemId = `root_property_${Math.floor(Math.random() * 10000)}`;

    const newItem = {
      id: newItemId,
      name: 'item',
      type: 'string',
      description: '',
      required: false,
      children: []
    };

    const updatedItems = [...treeItems, newItem];
    setTreeItems(updatedItems);
    setSelectedItemId(newItemId);

    const newSchema = {
      ...schema,
      ...treeItemsToSchema(updatedItems)
    };
    setSchema(newSchema);
    if (onChange) {
      onChange(newSchema);
    }
    return newItemId;
  }, [onChange, schema, treeItems]);

  const handleDeleteItem = useCallback(
    (itemId) => {
      const updatedItems = deleteItemFromTree(treeItems, itemId);
      setTreeItems(updatedItems);

      if (selectedItemId === itemId) {
        setSelectedItemId(null);
      }

      const newSchema = {
        ...schema,
        ...treeItemsToSchema(updatedItems)
      };
      setSchema(newSchema);
      if (onChange) {
        onChange(newSchema);
      }
    },
    [deleteItemFromTree, onChange, schema, selectedItemId, treeItems]
  );

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFile(file);
    }
  };

  const [generateSchema] = useGenerateMutation();
  const [isGeneratingSchema, setIsGeneratingSchema] = useState(false);
  const handleFile = async (file) => {
    setIsGeneratingSchema(true);
    let newSchema;
    try {
      newSchema = await fileToJsonSchema(file);
    } catch {
      const request = { promptCode: 'DEFAULT_SCHEMA_EXTRACTION_PROMPT' };
      const response = await generateSchema({ file, request }).unwrap();
      const cleanedResponse = response.replace(/```(?:yaml|yml)?\n?/g, '').replace(/```$/g, '');

      newSchema = yaml.load(cleanedResponse);
    }
    setIsGeneratingSchema(false);
    const items = schemaToTreeItems(newSchema);

    if (items.length === 0) {
      addNotification({
        type: 'warning',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.assistant.flow.generate.schema.empty' })
      });
    }

    setTreeItems(items);
    setSchema({
      ...schema,
      ...treeItemsToSchema(items),
      title: newSchema?.title,
      description: newSchema?.description
    });
  };

  const handleAddItem = useCallback(
    (parentId) => {
      const newItemId = `${parentId}.new_${Math.floor(Math.random() * 10000)}`;
      const parentItem = findItemById(treeItems, parentId);
      let defaultItemType = 'string';

      if (parentItem.type === 'array') {
        defaultItemType = 'object';
      }

      const newItem = {
        id: newItemId,
        name: 'item',
        type: defaultItemType,
        description: '',
        required: false,
        children: []
      };

      const updatedItems = addItemToTree(treeItems, parentId, newItem);
      setTreeItems(updatedItems);
      setSelectedItemId(newItemId);

      const newSchema = {
        ...schema,
        ...treeItemsToSchema(updatedItems)
      };
      setSchema(newSchema);
      if (onChange) {
        onChange(newSchema);
      }
      return newItemId;
    },
    [addItemToTree, findItemById, onChange, schema, treeItems]
  );

  const selectedItem = useMemo(
    () => (selectedItemId ? findItemById(treeItems, selectedItemId) : null),
    [findItemById, selectedItemId, treeItems]
  );

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFile(file);
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'column' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.yaml,.yml,.txt,.csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isGeneratingSchema && fileInputRef.current?.click()}
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80px',
          border: `2px dashed ${isDragOver ? '#1976d2' : '#9e9e9e'}`,
          borderRadius: '8px',
          backgroundColor: isDragOver ? '#e3f2fd' : 'transparent',
          transition: 'all 0.2s ease-in-out',
          cursor: isGeneratingSchema ? 'not-allowed' : 'pointer',
          opacity: isGeneratingSchema ? 0.6 : 1
        }}
      >
        <AttachFileIcon sx={{ fontSize: 40, color: isDragOver ? '#1976d2' : '#9e9e9e' }} />
        <MDTypography variant="body2" color={isDragOver ? 'primary' : 'secondary'}>
          {isGeneratingSchema
            ? 'Analyse en cours, veuillez patienter'
            : msg({ id: 'dropzone.dropfile.text' })}
        </MDTypography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
        <Stack direction="row" spacing={2}>
          <Controller
            name="format"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth margin="normal" sx={{ flex: 0.125 }}>
                <InputLabel>{msg({ id: 'field.output.format.label' })}</InputLabel>
                <Select {...field} label={msg({ id: 'field.output.format.label' })}>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="xml">XML</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="yaml">YAML</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={msg({ id: 'field.output.name.label' })}
                variant="outlined"
                fullWidth
                margin="normal"
                sx={{ flex: 0.25 }}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={msg({ id: 'field.output.description.label' })}
                variant="outlined"
                fullWidth
                margin="normal"
                sx={{ flex: 0.625 }}
              />
            )}
          />
        </Stack>
      </Box>

      <MDBox sx={{ display: 'flex', mt: 2, flex: 1, overflow: 'hidden' }}>
        <MDBox sx={{ display: 'flex', flexDirection: 'column', flex: 0.6 }}>
          <Typography variant="h6" gutterBottom ml={1}>
            {msg({ id: 'output.format.data.title' })}
          </Typography>
          <StructuredOutputTree
            items={treeItems}
            onSelectItem={handleSelectItem}
            onAddItem={handleAddItem}
            onDeleteItem={handleDeleteItem}
            onAddRootItem={handleAddRootItem}
            selectedItemId={selectedItemId}
          />
        </MDBox>

        <MDBox sx={{ display: 'flex', flexDirection: 'column', flex: 0.4, ml: 2 }}>
          <Typography variant="h6" gutterBottom ml={1}>
            {msg({ id: 'output.format.edit.data.title' })}
          </Typography>
          <Paper elevation={1} sx={{ px: 2 }}>
            <StructuredOutputForm
              selectedItem={selectedItem}
              items={treeItems}
              onUpdateItem={handleUpdateItem}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </Box>
  );
}

StructuredOutputBuilder.propTypes = {
  initialSchema: PropTypes.object,
  onChange: PropTypes.func
};
